// @flow

import React, { useMemo } from 'react';

import classNames from '@ta/common.classnames';
import { globalize, localize } from '@ta/common.i18n';
import { featureIsEnabled } from '@ta/common.features';
import { Link } from '@ta/common.routing';
import { Icon } from '@ta/styleguide.icon';
import SearchRecordTracker from '@ta/search.tracking';
import { InteractionLogger } from '@ta/tracking.interactions';

import styles from './ContinueSearchSection.less';

type SearchResult = {|
  filterCount: ?number,
  fromDate: ?string,
  searchDate: ?string,
  toDate: ?string,
  locationType: ?(
    | 'ATTRACTION'
    | 'ATTRACTION_PRODUCT'
    | 'GEO'
    | 'HOTEL'
    | 'NEIGHBORHOOD'
    | 'RESTAURANT'
  ),
  route: ?{|
    url: ?string,
  |},
|};

type Props = {|
  title: string,
  result: SearchResult,
  shelfKey: string,
|};

const ContinueSearchSection = ({ title, result, shelfKey }: Props) => {
  const { filterCount, fromDate, toDate, locationType, route } = result;

  const getLocalDateFromString = (selectedDate: string) => {
    const splitDate = selectedDate.split('-');
    // Note: Month value is offset by -1 because JS Dates use zero-indexed month values
    return new Date(
      parseInt(splitDate[0], 10),
      parseInt(splitDate[1], 10) - 1,
      parseInt(splitDate[2], 10),
    );
  };

  let searchDateParamString;
  if (toDate && fromDate) {
    searchDateParamString = localize('date_range_medium_month_to_day_of_month', {
      sDateStart: globalize.formatDate('date_medium_month', getLocalDateFromString(fromDate)),
      nDayEnd: globalize.formatDate('date_medium_month', getLocalDateFromString(toDate)),
    });
  }

  // For the single search test (NAV-9830) replace the search icon here with a placetype-specific
  // icon, to avoid repeating the same icon on the page.
  const iconName = useMemo(() => {
    if (
      locationType == null ||
      !(
        featureIsEnabled('cx_single_search_variant2') ||
        featureIsEnabled('cx_single_search_variant3')
      )
    ) {
      return 'search';
    }

    switch (locationType) {
      case 'ATTRACTION':
      case 'ATTRACTION_PRODUCT':
        return 'attractions';
      case 'GEO':
        return 'map-pin-fill';
      case 'HOTEL':
        return 'hotels';
      case 'NEIGHBORHOOD':
        return 'neighborhoods-fill';
      case 'RESTAURANT':
        return 'restaurants';
      default:
        throw new Error(`Invalid locationType: ${(locationType: empty)}`);
    }
  }, [locationType]);

  return (
    <SearchRecordTracker
      useSpan
      module="Search_Results"
      action="Search_Results_Selection"
      context={`${shelfKey} | card`}
    >
      <InteractionLogger>
        {logFeedClick => (
          <Link
            href={route?.url}
            className={classNames('ui_link', styles.section)}
            onClick={e => {
              logFeedClick(e, 'feedClick');
            }}
          >
            <Icon name={iconName} className={styles.icon} />
            <span className={styles.wrap}>
              <span className={styles.title}>{title}</span>

              {searchDateParamString != null && (
                <span className={styles.text}>{searchDateParamString}</span>
              )}

              {!!filterCount && (
                <>
                  {searchDateParamString != null && (
                    <span className={styles.text}>&nbsp;&middot;&nbsp;</span>
                  )}

                  <span className={styles.text}>
                    {localize('social_category_search_num_filters', {
                      '0': filterCount,
                    })}
                  </span>
                </>
              )}
            </span>
          </Link>
        )}
      </InteractionLogger>
    </SearchRecordTracker>
  );
};

export default ContinueSearchSection;
